import React, { useMemo, useState } from "react";
import { Alert, Button, Card, CardBody, Spinner, Typography } from "@material-tailwind/react";
import { CalendarDaysIcon, ChartBarSquareIcon, FolderIcon, ShieldCheckIcon } from "@heroicons/react/24/solid";
import { Navigate, useLocation } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";
import FormField from "../components/FormField";
import { useAuth } from "../context/AuthContext";

const FEATURES = [
  {
    icon: ChartBarSquareIcon,
    title: "الدخل والمصاريف",
    desc: "تسجيل سريع وتحليل يومي وأسبوعي وشهري."
  },
  {
    icon: FolderIcon,
    title: "المشاريع والدفعات",
    desc: "قيمة المشروع، المستلم، المتبقي، والمهام."
  },
  {
    icon: CalendarDaysIcon,
    title: "دفتر المواعيد",
    desc: "تنظيم الاجتماعات والمتابعات على شكل واضح."
  }
];

export default function LoginPage() {
  const location = useLocation();
  const { user, login, loading } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const redirectTo = useMemo(() => location.state?.from || "/dashboard", [location.state]);

  if (user && !loading) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      setBusy(true);
      await login(form.email, form.password);
    } catch (err) {
      const code = err?.code || "";
      if (code.includes("invalid-credential") || code.includes("wrong-password")) {
        setError("البريد الإلكتروني أو كلمة المرور غير صحيحة.");
      } else if (code.includes("too-many-requests")) {
        setError("تم إيقاف المحاولات مؤقتاً بسبب كثرة المحاولات. حاول لاحقاً.");
      } else {
        setError("تعذر تسجيل الدخول حالياً. تحقق من إعدادات Firebase.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-softbg">
      <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-center gap-5 px-3 py-5 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
        <div className="order-2 lg:order-1">
          <div className="glass-card overflow-hidden bg-gradient-to-br from-brand-800 via-brand-700 to-brand-900 p-5 text-white sm:p-7">
            <BrandLogo />
            <div className="mt-6 max-w-2xl">
              <h1 className="text-2xl font-extrabold leading-tight sm:text-4xl">
                نظام شخصي احترافي لإدارة
                <span className="text-accent-400"> المصاريف والدخل والمشاريع </span>
                على الهاتف بشكل واضح وآمن
              </h1>

            </div>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {FEATURES.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="rounded-[22px] border border-white/10 bg-white/10 p-4 backdrop-blur">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="font-extrabold">{item.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-white/75">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <Card className="mx-auto w-full max-w-md rounded-[28px] border border-white/80 shadow-brand">
            <CardBody className="p-5 sm:p-7">
              <div className="mb-6 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-[22px] bg-brand-50 text-brand-700">
                  <ShieldCheckIcon className="h-8 w-8" />
                </div>
                <h4 className="font-extrabold text-brand-700 text-xl sm:text-2xl">
                  تسجيل الدخول
                </h4>

              </div>

              {error ? (
                <Alert color="red" className="mb-5 rounded-2xl text-sm">
                  {error}
                </Alert>
              ) : null}

              <form onSubmit={handleSubmit} className="space-y-4">
                <FormField
                  type="email"
                  dir="ltr"
                  label="البريد الإلكتروني"
                  placeholder="name@example.com"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  required
                />

                <FormField
                  type="password"
                  dir="ltr"
                  label="كلمة المرور"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={form.password}
                  onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                  required
                />

                <Button type="submit" fullWidth className="rounded-2xl bg-brand-700 py-3 text-sm font-extrabold shadow-lg hover:bg-brand-800" disabled={busy}>
                  {busy ? (
                    <span className="flex items-center justify-center gap-2">
                      <Spinner className="h-4 w-4" />
                      جاري الدخول...
                    </span>
                  ) : (
                    "دخول إلى النظام"
                  )}
                </Button>
              </form>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
